import React from 'react'
import ReactDOM from 'react-dom/client'
import mapboxgl from 'mapbox-gl'
import App from './App.jsx'
import DemoApp from './demo/DemoApp.jsx'
import MarketingApp from './marketing/MarketingApp.jsx'
import VanirApp from './vanir/VanirApp.jsx'
import FinnishSitesApp from './finnish/FinnishSitesApp.jsx'
import { OrgProvider } from './contexts/OrgContext'
import 'mapbox-gl/dist/mapbox-gl.css'
import './index.css'

// mapbox-gl 3.1 has a bug where Map.removeSource always calls _updateTerrain,
// and that method blows up on `undefined.get` when its internal state is stale
// (mapbox-gl-js#12352, fixed in 3.7+). StrictMode's double-invoke of effects
// reliably hits it. Wrap _updateTerrain in try/catch so the cleanup error
// stays a console warning instead of corrupting the map and blacking out
// the page. Drop this patch once mapbox-gl is bumped to >= 3.7.
const _origUpdateTerrain = mapboxgl.Map.prototype._updateTerrain;
mapboxgl.Map.prototype._updateTerrain = function (...args) {
  try {
    return _origUpdateTerrain.apply(this, args);
  } catch (e) {
    console.warn('[mapbox-patch] swallowed _updateTerrain error:', e);
  }
};

// SPEC_DEMO.md PR-5. Path-based root dispatch — extended to 3 bundles:
//
//   /demo*  → DemoApp       (anonymous, pre-recorded fixture replay)
//   /app*   → App           (authed product: map, projects, etc.)
//   /, etc. → MarketingApp  (public marketing pages: home, pricing, book)
//
// Hash routing inside <App /> is preserved — `/app#/projects`,
// `/app#/projects/123`, etc. all keep working since hash navigation
// doesn't change the pathname.
//
// The demo lives at the real path /demo (not a hash route) so nginx
// can apply per-location CSP — the deny on / and the permit on /demo
// landed together in PR-3. Any in-app navigation BETWEEN bundles
// (marketing ↔ product, product ↔ demo) triggers a full page reload,
// which is exactly the security boundary we want (clean session
// unload). Do not replace this with a client-side router. See
// SPEC_DEMO.md → Implementation Notes → "no in-app navigation
// between /demo and the authenticated app" — the marketing bundle
// inherits the same constraint.
// Subdomain detection for org-specific apps
const hostname = window.location.hostname;
const subdomainMatch = hostname.match(/^([a-z0-9-]+)\.seidr\.(makers\.bot|site)$/);
const orgSlug = subdomainMatch ? subdomainMatch[1] : null;

// Org-specific app overrides (add more as needed)
const ORG_APPS = {
  vanir: VanirApp,
};

const pathname = window.location.pathname;
let Root;
if (pathname.startsWith('/demo'))           Root = DemoApp;
else if (pathname.startsWith('/finnish'))   Root = FinnishSitesApp;
else if (pathname.startsWith('/app')) {
  // Check for org-specific app override
  Root = (orgSlug && ORG_APPS[orgSlug]) ? ORG_APPS[orgSlug] : App;
}
else                                        Root = MarketingApp;

// Wrap with OrgProvider for subdomain branding
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <OrgProvider>
      <Root />
    </OrgProvider>
  </React.StrictMode>,
)
